// src/routes/routes.tsx
import { Navigate } from "react-router-dom";
import RouteGuard from "../guards/RouteGuard";
import AuthGuard from "../components/authentication/AuthGuard";
import GuestGuard from "../components/authentication/GuestGuard";
import DashboardLayout from "../components/Layouts/DashboardLayout";
import LazyLoader from "./routes-loader";
import {
  ACCESS_TOKEN_KEY,
  USER_TYPE_SYSTEM_ADMIN,
  USER_TYPE_EDIFY_STAFF,
  USER_TYPE_PARISH_COORDINATOR,
  USER_TYPE_SCHOOL_PROPRIETOR,
  USER_TYPE_SCHOOL_DIRECTOR,
} from "../api/constants";

// Root redirect component that checks authentication
const RootRedirect = () => {
  // Must match the key the auth context writes; this previously read
  // "accessToken" and so always redirected signed-in users to /login.
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const isAuthenticated = !!token;
  
  return isAuthenticated ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

const routes = [
  // =============== ROOT & PUBLIC ROUTES ===============
  { path: "/", element: <RootRedirect /> },
  { 
    path: "/login", 
    element: (
      <GuestGuard>
        <LazyLoader.Login />
      </GuestGuard>
    ) 
  },
  { 
    path: "/register", 
    element: (
      <GuestGuard>
        <LazyLoader.Register />
      </GuestGuard>
    ) 
  },
  { 
    path: "/forgot-password", 
    element: (
      <GuestGuard>
        <LazyLoader.ForgotPassword />
      </GuestGuard>
    ) 
  },
  { 
    path: "/reset-password/:token", 
    element: (
      <GuestGuard>
        <LazyLoader.ResetPassword />
      </GuestGuard>
    ) 
  },

  // =============== AUTHENTICATED ROUTES ===============
  {
    path: "/",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      // ========== Dashboard & Profile ==========
      { 
        path: "dashboard", 
        element: <LazyLoader.Dashboard /> 
      },
      { 
        path: "profile", 
        element: <LazyLoader.Profile /> 
      },

      // ========== Parishes ==========
      { 
        path: "parishes", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF, 
              USER_TYPE_PARISH_COORDINATOR
            ]}
          >
            <LazyLoader.ParishesList />
          </RouteGuard>
        )
      },
      { 
        path: "parishes/statistics", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF
            ]}
          >
            <LazyLoader.ParishStatistics />
          </RouteGuard>
        )
      },
      { 
        path: "parishes/:parishId", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF, 
              USER_TYPE_PARISH_COORDINATOR
            ]}
          >
            <LazyLoader.ParishDetails />
          </RouteGuard>
        )
      },

      // ========== Schools ==========
      {
        path: "schools",
        element: <LazyLoader.SchoolsList />
      },
      {
        path: "schools/statistics",
        element: <LazyLoader.SchoolStatistics />
      },
      {
        path: "schools/my-school",
        element: <LazyLoader.MySchool />
      },
      {
        path: "schools/performance",
        element: <LazyLoader.SchoolPerformance />
      },
      {
        path: "schools/:schoolId",
        element: <LazyLoader.SchoolDetails />
      },

      // ========== Meetings ==========
      { 
        path: "meetings", 
        element: <LazyLoader.MeetingsList /> 
      },
      { 
        path: "meetings/:meetingId", 
        element: <LazyLoader.MeetingDetails /> 
      },
      { 
        path: "meetings/new", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF, 
              USER_TYPE_PARISH_COORDINATOR
            ]}
          >
            <LazyLoader.MeetingForm />
          </RouteGuard>
        )
      },

      // ========== Assessments (SSA) - ENHANCED ==========
      { 
        path: "assessments", 
        element: <LazyLoader.AssessmentsList /> 
      },
      { 
        path: "assessments/:assessmentId", 
        element: <LazyLoader.AssessmentDetails /> 
      },
      { 
        path: "assessments/new", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF, 
              USER_TYPE_PARISH_COORDINATOR,
              USER_TYPE_SCHOOL_PROPRIETOR,
              USER_TYPE_SCHOOL_DIRECTOR
            ]}
          >
            <LazyLoader.AssessmentForm />
          </RouteGuard>
        )
      },
      // Indicators Page
      { 
        path: "assessments/indicators", 
        element: <LazyLoader.IndicatorsList /> 
      },
      // NEW: Indicator Management (Admin only)
      { 
        path: "assessments/indicators/manage", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF
            ]}
          >
            <LazyLoader.IndicatorManagement />
          </RouteGuard>
        )
      },
      { 
        path: "assessments/outcome-areas/manage", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF
            ]}
          >
            <LazyLoader.OutcomeAreaManagement />
          </RouteGuard>
        )
      },
      // Champion Schools
      { 
        path: "assessments/champions", 
        element: <LazyLoader.ChampionSchools /> 
      },
      // All Assessments (alternative route)
      { 
        path: "assessments/all", 
        element: <LazyLoader.AssessmentsList /> 
      },

      // ========== Training ==========
      { path: "training", element: <LazyLoader.TrainingModules /> },
      { path: "training/sessions", element: <LazyLoader.TrainingSessions /> },
      { path: "training/my-progress", element: <LazyLoader.MyProgress /> },
      {
        path: "training/manage",
        element: (
          <RouteGuard
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN,
              USER_TYPE_EDIFY_STAFF,
              USER_TYPE_PARISH_COORDINATOR,
            ]}
          >
            <LazyLoader.TrainingManage />
          </RouteGuard>
        ),
      },
      { path: "training/resources", element: <LazyLoader.TrainingResources /> },
      // Keep this last so it does not swallow the static paths above.
      { path: "training/:moduleId", element: <LazyLoader.TrainingModuleDetails /> },

      // ========== PLC ==========
      { path: "plc", element: <LazyLoader.PLCList mode="mine" /> },
      { path: "plc/browse", element: <LazyLoader.PLCList mode="browse" /> },
      { path: "plc/:groupId", element: <LazyLoader.PLCDetails /> },

      // ========== Help ==========
      // Documentation, available to every signed-in user regardless of role.
      { path: "guide", element: <LazyLoader.UserGuide /> },
      { path: "test", element: <LazyLoader.TestingGuide /> },

      // ========== Reports ==========
      { path: "reports", element: <LazyLoader.Reports /> },
      { path: "reports/parishes", element: <LazyLoader.Reports initialTab={1} /> },
      { path: "reports/schools", element: <LazyLoader.Reports initialTab={2} /> },
      { path: "reports/export", element: <LazyLoader.Reports initialTab={4} /> },
    ],
  },

  // =============== ADMIN ROUTES ===============
  {
    path: "/admin",
    element: (
      <AuthGuard>
        <DashboardLayout />
      </AuthGuard>
    ),
    children: [
      // ========== Parish Coordinators ==========
      { 
        path: "coordinators", 
        element: (
          <RouteGuard 
            requiredRoles={[
              USER_TYPE_SYSTEM_ADMIN, 
              USER_TYPE_EDIFY_STAFF
            ]}
          >
            <LazyLoader.CoordinatorsList />
          </RouteGuard>
        )
      },

      // ========== User Management ==========
      { 
        path: "users", 
        element: (
          <RouteGuard requiredRoles={[USER_TYPE_SYSTEM_ADMIN]}>
            <LazyLoader.UsersList />
          </RouteGuard>
        )
      },
      { 
        path: "users/:userId", 
        element: (
          <RouteGuard requiredRoles={[USER_TYPE_SYSTEM_ADMIN]}>
            <LazyLoader.UserDetails />
          </RouteGuard>
        )
      },
      
      // ========== Roles Management ==========
      { 
        path: "roles", 
        element: (
          <RouteGuard requiredRoles={[USER_TYPE_SYSTEM_ADMIN]}>
            <LazyLoader.RolesList />
          </RouteGuard>
        )
      },
      
      // ========== System Settings ==========
      { 
        path: "settings", 
        element: (
          <RouteGuard requiredRoles={[USER_TYPE_SYSTEM_ADMIN]}>
            <LazyLoader.SystemSettings />
          </RouteGuard>
        )
      },
      
      // ========== Financials ==========
      {
        path: "financials/subscriptions",
        element: (
          <RouteGuard
            requiredRoles={[USER_TYPE_SYSTEM_ADMIN, USER_TYPE_EDIFY_STAFF]}
          >
            <LazyLoader.Subscriptions />
          </RouteGuard>
        ),
      },
      {
        path: "financials/payments",
        element: (
          <RouteGuard
            requiredRoles={[USER_TYPE_SYSTEM_ADMIN, USER_TYPE_EDIFY_STAFF]}
          >
            <LazyLoader.Subscriptions view="payments" />
          </RouteGuard>
        ),
      },
      {
        path: "financials/reports",
        element: (
          <RouteGuard
            requiredRoles={[USER_TYPE_SYSTEM_ADMIN, USER_TYPE_EDIFY_STAFF]}
          >
            <LazyLoader.Subscriptions />
          </RouteGuard>
        ),
      },

      // ========== Audit Logs ==========
      { 
        path: "audit-logs", 
        element: (
          <RouteGuard requiredRoles={[USER_TYPE_SYSTEM_ADMIN]}>
            <LazyLoader.AuditLogs />
          </RouteGuard>
        )
      },
    ],
  },

  // =============== FALLBACK ROUTES ===============
  { 
    path: "/forbidden", 
    element: <LazyLoader.Error403 /> 
  },
  { 
    path: "*", 
    element: <LazyLoader.Error404 /> 
  },
];

export default routes;